/* eslint-disable no-restricted-syntax */
/* eslint-disable no-undef */
import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  notifications: [],       // claims notifications
  staffNotifications: [],  // collaboration / staff notifications (admin's view of staff activity)
  companyNotifications: [], // company-wide activity notifications (e.g. favorites)
  employeeNotifications: [], // the logged-in employee's own notifications (e.g. staff ratings, approvals)
  loading: false,
  isSuccess: false,
  errors: []
};

// Fetch claims notifications
export const fetchAdminNotification = createAsyncThunk(
  'adminNotification/fetchAdminNotification',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const company_id = localStorage.getItem('company');
      const response = await api.get(`/notification/company/${company_id}`, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch staff / collaboration notifications
export const fetchStaffNotifications = createAsyncThunk(
  'adminNotification/fetchStaffNotifications',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const company_id = localStorage.getItem('company');
      const token = localStorage.getItem('accessToken');
      const response = await api.get(
        `/notification/company/${company_id}/collaboration/staff`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch company-wide activity notifications (e.g. favorites)
export const fetchCompanyActivityNotifications = createAsyncThunk(
  'adminNotification/fetchCompanyActivityNotifications',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const company_id = localStorage.getItem('company');
      const token = localStorage.getItem('accessToken');
      const response = await api.get(
        `/notification/company/${company_id}/activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch the logged-in employee's own notifications (e.g. staff ratings, approvals)
export const fetchEmployeeNotifications = createAsyncThunk(
  'adminNotification/fetchEmployeeNotifications',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get('/notification/collaboration/employee', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Mark a notification as viewed
export const markAdminNotificationAsViewed = createAsyncThunk(
  'adminNotification/markAdminNotificationAsViewed',
  async (notifId, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.put(`/notification/${notifId}`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      dispatch(fetchAdminNotification());
      dispatch(fetchStaffNotifications());
      dispatch(fetchCompanyActivityNotifications());
      dispatch(fetchEmployeeNotifications());
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const adminNotificationSlice = createSlice({
  name: 'adminNotifications',
  initialState,
  extraReducers: (builder) => {
    builder
      // claims notifications
      .addCase(fetchAdminNotification.fulfilled, (state, { payload }) => {
        state.notifications = payload;
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(fetchAdminNotification.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(fetchAdminNotification.rejected, (state, { payload }) => {
        state.errors = ['Failed to fetch admin notifications.'];
        state.isSuccess = false;
        state.loading = false;
      })
      // staff notifications
      .addCase(fetchStaffNotifications.fulfilled, (state, { payload }) => {
        state.staffNotifications = payload;
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(fetchStaffNotifications.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(fetchStaffNotifications.rejected, (state) => {
        state.staffNotifications = [];
        state.isSuccess = false;
        state.loading = false;
      })
      // company-wide activity notifications
      .addCase(fetchCompanyActivityNotifications.fulfilled, (state, { payload }) => {
        state.companyNotifications = payload;
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(fetchCompanyActivityNotifications.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(fetchCompanyActivityNotifications.rejected, (state) => {
        state.companyNotifications = [];
        state.isSuccess = false;
        state.loading = false;
      })
      // the logged-in employee's own notifications
      .addCase(fetchEmployeeNotifications.fulfilled, (state, { payload }) => {
        state.employeeNotifications = payload;
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(fetchEmployeeNotifications.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(fetchEmployeeNotifications.rejected, (state) => {
        state.employeeNotifications = [];
        state.isSuccess = false;
        state.loading = false;
      })
      // mark as viewed
      .addCase(markAdminNotificationAsViewed.fulfilled, (state, { payload }) => {
        state.notifications = state.notifications.map((n) =>
          n._id === payload._id ? { ...n, view: true } : n
        );
        state.staffNotifications = state.staffNotifications.map((n) =>
          n._id === payload._id ? { ...n, view: true } : n
        );
        state.companyNotifications = state.companyNotifications.map((n) =>
          n._id === payload._id ? { ...n, view: true } : n
        );
        state.employeeNotifications = state.employeeNotifications.map((n) =>
          n._id === payload._id ? { ...n, view: true } : n
        );
        state.isSuccess = true;
        state.loading = false;
      });
  }
});

export default adminNotificationSlice.reducer;
